import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  Check,
  Handshake,
  Search,
  Sparkles,
  User,
} from 'lucide-react';
import { Button, Card, CardBody, Textarea } from '@nexply/ui';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { FloatingField } from '../components/FloatingField.js';
import { PhotoUpload } from '../components/PhotoUpload.js';
import { LegalFooter } from '../components/LegalFooter.js';

const STEPS = [
  { key: 'contact', label: 'Kontakt' },
  { key: 'social', label: 'Social' },
  { key: 'photo', label: 'Foto' },
  { key: 'work', label: 'Beruflich' },
  { key: 'matching', label: 'Matching' },
  { key: 'finish', label: 'Abschluss' },
] as const;

const LAST_STEP = STEPS.length - 1;

// Der Client exportiert den Eingabetyp nicht — er hängt am Contract, also hier ableiten.
type SubmitInput = Parameters<typeof api.join.submit>[1];

interface Form {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  linkedin: string;
  instagram: string;
  xing: string;
  website: string;
  photoUrl: string;
  company: string;
  role: string;
  industry: string;
  city: string;
  country: string;
  offering: string;
  seeking: string;
  pitch: string;
  tagsRaw: string;
  acceptTerms: boolean;
  newsletter: boolean;
  password: string;
}

const EMPTY: Form = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  linkedin: '',
  instagram: '',
  xing: '',
  website: '',
  photoUrl: '',
  company: '',
  role: '',
  industry: '',
  city: '',
  country: '',
  offering: '',
  seeking: '',
  pitch: '',
  tagsRaw: '',
  acceptTerms: false,
  newsletter: false,
  password: '',
};

/** Leere Optionalfelder gar nicht erst senden — der Contract erwartet sie schlicht nicht. */
function opt(v: string): string | undefined {
  const t = v.trim();
  return t ? t : undefined;
}

/** Drei mögliche Endzustände — 409 ist ein Erfolg für die Eintragung, nur nicht für das Konto. */
type Done =
  | { kind: 'with-account' }
  | { kind: 'without-account' }
  | { kind: 'email-taken' };

export function JoinPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const nav = useNavigate();
  const { user, loading: authLoading, refresh, logout } = useAuth();

  const hostQ = useQuery({
    // Die Antwort hängt davon ab, WER fragt (`viewer`). Ohne die Identität im Key würde
    // nach dem Anmelden die anonyme Antwort aus dem Cache weiterbenutzt — die Seite
    // zeigte dann den Wizard statt des Vernetzen-Knopfs.
    queryKey: ['join', 'host', slug, user?.id ?? 'anon'],
    queryFn: () => api.join.host(slug),
    retry: false,
  });

  // Der Formularstand lebt bewusst hier oben: Schritt-Wechsel, Zurück-Button und Fehler
  // dürfen niemals Eingaben verlieren.
  const [form, setForm] = useState<Form>(EMPTY);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState<1 | -1>(1);
  const [fieldErr, setFieldErr] = useState<Partial<Record<keyof Form, string>>>({});
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [connected, setConnected] = useState<'done' | 'already' | null>(null);
  const [connectErr, setConnectErr] = useState<string | null>(null);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErr((e) => ({ ...e, [k]: undefined }));
  };

  const submit = useMutation({
    mutationFn: (withAccount: boolean) => {
      const input: SubmitInput = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        linkedin: opt(form.linkedin),
        instagram: opt(form.instagram),
        xing: opt(form.xing),
        website: opt(form.website),
        photoUrl: opt(form.photoUrl),
        company: opt(form.company),
        role: opt(form.role),
        industry: opt(form.industry),
        city: opt(form.city),
        country: opt(form.country),
        offering: opt(form.offering),
        seeking: opt(form.seeking),
        pitch: opt(form.pitch),
        // Der Contract erlaubt max. 20 Stichworte à 60 Zeichen — hier hart kappen.
        tags: form.tagsRaw
          .split(',')
          .map((t) => t.trim().slice(0, 60))
          .filter(Boolean)
          .slice(0, 20),
        acceptTerms: true,
        newsletter: form.newsletter,
        // Ohne Passwort das Feld komplett weglassen — ein leerer String wäre 400.
        ...(withAccount ? { createAccount: { password: form.password } } : {}),
      };
      return api.join.submit(slug, input);
    },
    onSuccess: async (res) => {
      if (res.accountCreated) {
        await refresh();
        setDone({ kind: 'with-account' });
      } else {
        setDone({ kind: 'without-account' });
      }
    },
    onError: (err: { status?: number; code?: string; message?: string }) => {
      // 409: die Eintragung ist durch, nur das Konto existiert schon. Kein Fehlerzustand,
      // sondern ein eigener Abschluss — erneutes Absenden würde doppelt eintragen.
      if (err.status === 409 || err.code === 'email_taken') {
        setDone({ kind: 'email-taken' });
        return;
      }
      if (err.status === 429) {
        setSubmitErr(
          'Zu viele Versuche in kurzer Zeit. Warte bitte ein paar Minuten und schicke das Formular dann erneut ab — deine Eingaben bleiben erhalten.',
        );
        return;
      }
      if (err.status === 404) {
        setSubmitErr('Dieser Einladungslink existiert nicht mehr.');
        return;
      }
      setSubmitErr(err.message ?? 'Das Absenden hat nicht geklappt. Bitte versuche es erneut.');
    },
  });

  // --- Kurzweg für alle, die schon ein Konto haben ---
  const connect = useMutation({
    mutationFn: () => api.join.connect(slug),
    onSuccess: async (res) => {
      await refresh();
      setConnected(res.alreadyConnected ? 'already' : 'done');
    },
    onError: (err: { status?: number; code?: string; message?: string }) => {
      setConnectErr(err.message ?? 'Das Vernetzen hat nicht geklappt. Bitte versuche es erneut.');
    },
  });

  function validate(current: number): boolean {
    const e: Partial<Record<keyof Form, string>> = {};
    if (current === 0) {
      if (!form.firstName.trim()) e.firstName = 'Bitte gib deinen Vornamen an.';
      if (!form.lastName.trim()) e.lastName = 'Bitte gib deinen Nachnamen an.';
      if (!form.phone.trim()) e.phone = 'Bitte gib deine Telefonnummer an.';
      if (!form.email.trim()) e.email = 'Bitte gib deine E-Mail-Adresse an.';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
        e.email = 'Diese E-Mail-Adresse sieht nicht gültig aus.';
    }
    if (current === LAST_STEP) {
      if (!form.acceptTerms) e.acceptTerms = 'Ohne Zustimmung können wir dich nicht eintragen.';
    }
    setFieldErr(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (!validate(step)) return;
    setDir(1);
    setStep((s) => Math.min(LAST_STEP, s + 1));
  }

  function back() {
    setSubmitErr(null);
    setDir(-1);
    setStep((s) => Math.max(0, s - 1));
  }

  function send(withAccount: boolean) {
    setSubmitErr(null);
    if (!validate(LAST_STEP)) return;
    if (withAccount && form.password.length < 8) {
      setFieldErr((e) => ({ ...e, password: 'Das Passwort braucht mindestens 8 Zeichen.' }));
      return;
    }
    submit.mutate(withAccount);
  }

  if (hostQ.isError) {
    return (
      <Shell>
        <Card>
          <CardBody className="py-16 text-center">
            <div className="text-4xl mb-3">🔍</div>
            <h1 className="text-lg font-medium text-text">Diesen Link gibt es nicht</h1>
            <p className="mt-2 text-sm text-text-muted">
              Der Einladungslink ist ungültig oder wurde zurückgezogen. Frag am besten kurz bei der
              Person nach, die ihn dir gegeben hat.
            </p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  // `authLoading` gehört dazu: Solange offen ist, ob jemand angemeldet ist, würde sonst
  // für einen Moment der Wizard erscheinen und dann zur Vernetzen-Ansicht springen.
  if (authLoading || hostQ.isLoading || !hostQ.data) {
    return (
      <Shell>
        <div className="h-64 rounded-2xl border border-line bg-raise animate-pulse" />
      </Shell>
    );
  }

  const host = hostQ.data.host;

  if (done) {
    return (
      <Shell>
        <Success done={done} hostName={host.name} onApp={() => nav({ to: '/app' })} />
      </Shell>
    );
  }

  // Wer angemeldet ist, hat seine Daten schon im Konto. Ihn durch sechs Schritte zu
  // schicken, wäre reine Tipparbeit für Angaben, die längst existieren.
  if (hostQ.data.viewer) {
    return (
      <Shell>
        <ConnectPanel
          host={host}
          viewer={hostQ.data.viewer}
          state={connected}
          error={connectErr}
          busy={connect.isPending}
          onConnect={() => {
            setConnectErr(null);
            connect.mutate();
          }}
          onApp={() => nav({ to: '/app' })}
          onProfile={() => nav({ to: '/profile' })}
          onSwitchAccount={async () => {
            try {
              await logout();
            } catch {
              /* Abmelden darf den Wechsel nicht blockieren. */
            }
            await hostQ.refetch();
          }}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <HostHeader host={host} />

      {/* Der Ausweg für Bestandsnutzer — sonst tippen sie das Formular durch und laufen
          am Ende in "E-Mail bereits vergeben". */}
      <div className="mt-3 rounded-xl border border-line bg-surface-1 px-4 py-3 text-center text-xs text-text-muted">
        Du hast schon ein Nexply-Konto?{' '}
        <Link
          to="/login"
          search={{ next: `/join/${slug}` }}
          className="text-gold underline underline-offset-2 hover:text-gold-light"
        >
          Anmelden und mit einem Klick vernetzen
        </Link>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-text-muted">
          <span>
            Schritt {step + 1} von {STEPS.length}
          </span>
          <span className="text-gold/80">{STEPS[step]?.label}</span>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-raise overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-gold-dark to-gold-light"
            animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          />
        </div>
      </div>

      <div className="mt-5 overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: dir * 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -24 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {step === 0 && (
              <StepCard
                title="Wie erreichen wir dich?"
                hint="Diese vier Angaben brauchen wir — alles danach ist freiwillig."
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field
                    id="firstName"
                    label="Vorname"
                    maxLength={80}
                    value={form.firstName}
                    onChange={(v) => set('firstName', v)}
                    autoComplete="given-name"
                    error={fieldErr.firstName}
                  />
                  <Field
                    id="lastName"
                    label="Nachname"
                    maxLength={80}
                    value={form.lastName}
                    onChange={(v) => set('lastName', v)}
                    autoComplete="family-name"
                    error={fieldErr.lastName}
                  />
                </div>
                <Field
                  id="email"
                  label="E-Mail"
                  type="email"
                  inputMode="email"
                  value={form.email}
                  onChange={(v) => set('email', v)}
                  autoComplete="email"
                  error={fieldErr.email}
                />
                <Field
                  id="phone"
                  label="Telefonnummer"
                  type="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(v) => set('phone', v)}
                  autoComplete="tel"
                  error={fieldErr.phone}
                />
              </StepCard>
            )}

            {step === 1 && (
              <StepCard
                title="Wo findet man dich sonst?"
                hint="Optional. Macht es leichter, dich später wiederzufinden."
              >
                <Field
                  id="linkedin"
                  label="LinkedIn"
                  value={form.linkedin}
                  onChange={(v) => set('linkedin', v)}
                />
                <Field
                  id="instagram"
                  label="Instagram"
                  value={form.instagram}
                  onChange={(v) => set('instagram', v)}
                />
                <Field id="xing" label="XING" value={form.xing} onChange={(v) => set('xing', v)} />
                <Field
                  id="website"
                  label="Website"
                  value={form.website}
                  onChange={(v) => set('website', v)}
                />
              </StepCard>
            )}

            {step === 2 && (
              <StepCard
                title="Ein Gesicht zum Namen"
                hint="Optional — aber ein Foto wird deutlich häufiger wiedererkannt."
              >
                <div className="flex justify-center py-2">
                  <PhotoUpload
                    value={form.photoUrl}
                    onChange={(v) => set('photoUrl', v)}
                    fallbackInitials={
                      `${form.firstName[0] ?? ''}${form.lastName[0] ?? ''}`.toUpperCase() || '?'
                    }
                  />
                </div>
              </StepCard>
            )}

            {step === 3 && (
              <StepCard title="Was machst du beruflich?" hint="Optional, hilft aber beim Zuordnen.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field
                    id="company"
                    label="Firma"
                    value={form.company}
                    onChange={(v) => set('company', v)}
                  />
                  <Field id="role" label="Rolle" value={form.role} onChange={(v) => set('role', v)} />
                  <Field
                    id="industry"
                    label="Branche"
                    value={form.industry}
                    onChange={(v) => set('industry', v)}
                  />
                  <Field id="city" label="Stadt" value={form.city} onChange={(v) => set('city', v)} />
                  <Field
                    id="country"
                    label="Land"
                    value={form.country}
                    onChange={(v) => set('country', v)}
                  />
                </div>
              </StepCard>
            )}

            {step === 4 && (
              <StepCard
                title="Der Teil, auf den es ankommt"
                hint="Auf genau diesen Feldern läuft das KI-Matching. Je konkreter du hier bist, desto besser sind die Kontakte, die dir vorgeschlagen werden."
              >
                <LabeledArea
                  id="offering"
                  icon={<Handshake className="h-4 w-4 text-gold" />}
                  label="Was bietest du an?"
                  placeholder="z. B. Webentwicklung für Handwerksbetriebe, Zugang zu Investoren im Food-Bereich …"
                  value={form.offering}
                  onChange={(v) => set('offering', v)}
                />
                <LabeledArea
                  id="seeking"
                  icon={<Search className="h-4 w-4 text-gold" />}
                  label="Was suchst du?"
                  placeholder="z. B. Vertriebspartner in Bayern, einen Steuerberater für GmbHs …"
                  value={form.seeking}
                  onChange={(v) => set('seeking', v)}
                />
                <LabeledArea
                  id="pitch"
                  icon={<Sparkles className="h-4 w-4 text-gold" />}
                  label="Dein Pitch in zwei Sätzen"
                  placeholder="Wer bist du, und warum sollte man mit dir sprechen?"
                  value={form.pitch}
                  onChange={(v) => set('pitch', v)}
                />
                <Field
                  id="tags"
                  label="Stichworte (mit Komma trennen)"
                  value={form.tagsRaw}
                  onChange={(v) => set('tagsRaw', v)}
                  hint="z. B. KI, B2B, Immobilien"
                />
              </StepCard>
            )}

            {step === 5 && (
              <div className="space-y-4">
                <StepCard title="Fast geschafft" hint={`Noch ein Haken — dann bist du im Netzwerk von ${host.name}.`}>
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.acceptTerms}
                      onChange={(e) => set('acceptTerms', e.currentTarget.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 accent-gold"
                    />
                    <span className="text-sm text-text-dim leading-relaxed">
                      Ich stimme den{' '}
                      <a
                        href="/agb"
                        target="_blank"
                        rel="noreferrer"
                        className="text-gold-light underline"
                      >
                        Nutzungsbedingungen
                      </a>{' '}
                      zu und habe die{' '}
                      <a
                        href="/datenschutz"
                        target="_blank"
                        rel="noreferrer"
                        className="text-gold-light underline"
                      >
                        Datenschutzerklärung
                      </a>{' '}
                      sowie das{' '}
                      <a
                        href="/impressum"
                        target="_blank"
                        rel="noreferrer"
                        className="text-gold-light underline"
                      >
                        Impressum
                      </a>{' '}
                      gelesen. <span className="text-danger">*</span>
                    </span>
                  </label>
                  {fieldErr.acceptTerms && (
                    <div className="text-xs text-danger">{fieldErr.acceptTerms}</div>
                  )}

                  <label className="flex gap-3 items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.newsletter}
                      onChange={(e) => set('newsletter', e.currentTarget.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 accent-gold"
                    />
                    <span className="text-sm text-text-dim leading-relaxed">
                      Schickt mir gelegentlich Neuigkeiten per E-Mail (jederzeit abbestellbar).
                    </span>
                  </label>
                </StepCard>

                <div className="rounded-2xl border border-gold/30 bg-gold/[0.06] p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-gold" />
                    <h2 className="text-base font-semibold text-text">
                      Dein Profil ist fertig. Willst du es behalten?
                    </h2>
                  </div>
                  <p className="text-sm text-text-dim leading-relaxed">
                    Du hast gerade alles eingegeben, was ein nexply-Profil braucht. Setz ein
                    Passwort — und aus deiner Eintragung wird dein eigenes Konto. Ein Feld, mehr
                    nicht.
                  </p>
                  <ul className="space-y-1.5 text-sm text-text-dim">
                    {[
                      'Dein eigenes Netzwerk — jeder Kontakt an einem Ort.',
                      'KI-Matching auf Basis von "biete" und "suche" — genau dem, was du oben geschrieben hast.',
                      'Dein eigener Einladungslink samt QR-Code fürs nächste Event.',
                    ].map((b) => (
                      <li key={b} className="flex gap-2">
                        <Check className="h-4 w-4 shrink-0 text-success mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Field
                    id="password"
                    label="Passwort (mind. 8 Zeichen)"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(v) => set('password', v)}
                    error={fieldErr.password}
                  />
                  <Button
                    size="lg"
                    fullWidth
                    loading={submit.isPending}
                    onClick={() => send(true)}
                  >
                    Konto anlegen &amp; eintragen
                  </Button>
                  <button
                    type="button"
                    disabled={submit.isPending}
                    onClick={() => send(false)}
                    className="w-full text-center text-sm text-text-muted hover:text-text transition disabled:opacity-40"
                  >
                    Nein danke — nur eintragen
                  </button>
                </div>

                {submitErr && (
                  <div className="rounded-xl border border-danger/25 bg-danger/10 px-4 py-3 text-sm text-danger">
                    {submitErr}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-5 flex items-center gap-3">
        {step > 0 && (
          <Button variant="ghost" onClick={back} disabled={submit.isPending}>
            <ArrowLeft className="h-4 w-4" />
            Zurück
          </Button>
        )}
        {step < LAST_STEP && (
          <div className="ml-auto flex items-center gap-3">
            {step > 0 && step < 5 && (
              <button
                type="button"
                onClick={() => {
                  setDir(1);
                  setStep((s) => s + 1);
                }}
                className="text-sm text-text-muted hover:text-text transition"
              >
                Überspringen
              </button>
            )}
            <Button onClick={next}>Weiter</Button>
          </div>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh bg-ink text-text">
      <header className="border-b border-line bg-ink-2/60 backdrop-blur">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center justify-between">
          <span className="text-lg font-light tracking-tight text-gold">nexply</span>
          <span className="text-[10px] uppercase tracking-[0.25em] text-text-muted">Eintragen</span>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 py-6 pb-[calc(env(safe-area-inset-bottom)+32px)]">
        {children}
        <LegalFooter className="mt-10" />
      </main>
    </div>
  );
}

/**
 * Die Ansicht für angemeldete Besucher: bestätigen und vernetzen, statt sechs Schritte
 * Formular. Zeigt bewusst, welche eigenen Daten dabei beim Gastgeber landen — vernetzen
 * heißt Daten teilen, und das soll niemand versehentlich tun.
 */
function ConnectPanel({
  host,
  viewer,
  state,
  error,
  busy,
  onConnect,
  onApp,
  onProfile,
  onSwitchAccount,
}: {
  host: { name: string; company: string | null; role: string | null; photoUrl: string | null; pitch: string | null };
  viewer: { name: string; email: string; photoUrl: string | null; connected: boolean; isSelf: boolean };
  state: 'done' | 'already' | null;
  error: string | null;
  busy: boolean;
  onConnect: () => void;
  onApp: () => void;
  onProfile: () => void;
  onSwitchAccount: () => void;
}) {
  // Der eigene Link — hier gibt es nichts zu vernetzen.
  if (viewer.isSelf) {
    return (
      <Card>
        <CardBody className="py-14 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-raise text-text-muted">
            <Handshake className="h-7 w-7" />
          </div>
          <h1 className="mt-4 text-lg font-medium text-text">Das ist dein eigener Einladungslink</h1>
          <p className="mx-auto mt-2 max-w-sm text-sm text-text-muted">
            So sehen ihn die Leute, denen du ihn schickst — nur dass bei ihnen hier ein Formular
            steht oder, wenn sie schon ein Konto haben, ein Vernetzen-Knopf.
          </p>
          <Button className="mt-6" onClick={onApp}>
            Zurück zur App
          </Button>
        </CardBody>
      </Card>
    );
  }

  const alreadyConnected = state !== null || viewer.connected;

  if (alreadyConnected) {
    const fresh = state === 'done';
    return (
      <Card>
        <CardBody className="py-14 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gold/10 text-gold">
            <Check className="h-7 w-7" />
          </div>
          <h1 className="mt-4 text-lg font-medium text-text">
            {fresh ? `Ihr seid vernetzt` : `Ihr seid bereits vernetzt`}
          </h1>
          <p className="mx-auto mt-2 max-w-sm text-sm text-text-muted">
            {fresh
              ? `${host.name} ist ab jetzt in deinem Netzwerk — und du in ${host.name}s Netzwerk. Beide Seiten sehen einander.`
              : `${host.name} liegt schon in deinem Netzwerk und du in ${host.name}s. Es gibt nichts mehr zu tun.`}
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button onClick={onApp}>Zu meinem Netzwerk</Button>
          </div>
          {/* Ein Angebot, keine Bedingung — vernetzt ist man an dieser Stelle längst. */}
          <button
            type="button"
            onClick={onProfile}
            className="mt-4 text-xs text-text-muted underline underline-offset-2 hover:text-text-dim"
          >
            Profil vervollständigen
          </button>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <HostHeader host={host} showFormHint={false} />

      <Card className="mt-4">
        <CardBody>
          <div className="flex items-center gap-3">
            {viewer.photoUrl ? (
              <img
                src={viewer.photoUrl}
                alt=""
                className="h-11 w-11 shrink-0 rounded-full object-cover border border-line bg-surface-2"
              />
            ) : (
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-raise text-text-muted">
                <User className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Angemeldet als</div>
              <div className="truncate text-sm font-medium text-text">{viewer.name}</div>
              <div className="truncate text-xs text-text-muted">{viewer.email}</div>
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-line bg-raise/50 p-4">
            <div className="flex items-start gap-2.5">
              <Handshake className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <p className="text-sm leading-relaxed text-text-dim">
                Du musst nichts noch einmal eintippen. Mit einem Klick landet dein Profil bei{' '}
                <span className="text-text">{host.name}</span> — und {host.name} in deinem Netzwerk.
                Die Verbindung gilt in beide Richtungen.
              </p>
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}

          <Button className="mt-5 w-full" onClick={onConnect} disabled={busy}>
            {busy ? 'Wird vernetzt …' : `Mit ${host.name} vernetzen`}
          </Button>

          <button
            type="button"
            onClick={onSwitchAccount}
            className="mt-3 w-full text-center text-xs text-text-muted underline underline-offset-2 hover:text-text-dim"
          >
            Nicht du? Abmelden und neu eintragen
          </button>
        </CardBody>
      </Card>
    </>
  );
}

function HostHeader({
  host,
  showFormHint = true,
}: {
  host: { name: string; company: string | null; role: string | null; photoUrl: string | null; pitch: string | null };
  /** Der Hinweis auf das Formular — aus, wenn stattdessen mit einem Klick vernetzt wird. */
  showFormHint?: boolean;
}) {
  const subline = [host.role, host.company].filter(Boolean).join(' · ');
  return (
    <div className="rounded-2xl border border-line bg-surface-1 p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-gold/80">Du wirst eingeladen von</div>
      <div className="mt-3 flex items-center gap-4">
        {host.photoUrl ? (
          <img
            src={host.photoUrl}
            alt=""
            className="h-16 w-16 shrink-0 rounded-full object-cover border border-line bg-surface-2"
          />
        ) : (
          <div className="h-16 w-16 shrink-0 grid place-items-center rounded-full bg-raise text-text-muted">
            <User className="h-7 w-7" />
          </div>
        )}
        <div className="min-w-0">
          <div className="text-xl font-semibold tracking-tight text-text">{host.name}</div>
          {subline && (
            <div className="mt-0.5 flex items-center gap-1.5 text-sm text-text-dim">
              <Building2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{subline}</span>
            </div>
          )}
        </div>
      </div>
      {host.pitch && (
        <p className="mt-4 text-sm text-text-dim leading-relaxed whitespace-pre-wrap">{host.pitch}</p>
      )}
      {showFormHint && (
        <p className="mt-4 text-xs text-text-muted">
          Trag dich in ein paar Schritten ein — du landest direkt im Netzwerk von {host.name}.
        </p>
      )}
    </div>
  );
}

function StepCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-line bg-surface-1 p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-text">{title}</h2>
        {hint && <p className="mt-1 text-sm text-text-muted leading-relaxed">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

// maxLength spiegelt die Contract-Grenzen: so entsteht gar nicht erst ein 400 ohne Feldbezug.
function Field({
  error,
  maxLength = 200,
  ...props
}: Parameters<typeof FloatingField>[0] & { error?: string }) {
  return (
    <div>
      <FloatingField maxLength={maxLength} {...props} />
      {error && <div className="mt-1.5 px-1 text-xs text-danger">{error}</div>}
    </div>
  );
}

function LabeledArea({
  id,
  icon,
  label,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  icon: ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium text-text mb-1.5">
        {icon}
        {label}
      </label>
      <Textarea
        id={id}
        rows={3}
        maxLength={2000}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </div>
  );
}

function Success({
  done,
  hostName,
  onApp,
}: {
  done: Done;
  hostName: string;
  onApp: () => void;
}) {
  if (done.kind === 'with-account') {
    return (
      <div className="rounded-2xl border border-success/30 bg-success/[0.07] p-6 text-center space-y-4">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success/15">
          <Check className="h-7 w-7 text-success" />
        </div>
        <h1 className="text-xl font-semibold text-text">Willkommen bei nexply</h1>
        <p className="text-sm text-text-dim leading-relaxed">
          Du bist im Netzwerk von {hostName} — und dein eigenes Konto steht auch schon. Du bist
          bereits angemeldet, dein Profil ist mit deinen Antworten gefüllt.
        </p>
        <Button size="lg" fullWidth onClick={onApp}>
          Zu meinem Netzwerk
        </Button>
      </div>
    );
  }

  if (done.kind === 'email-taken') {
    return (
      <div className="rounded-2xl border border-warn/30 bg-warn/[0.07] p-6 text-center space-y-4">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-warn/15">
          <Check className="h-7 w-7 text-warn" />
        </div>
        <h1 className="text-xl font-semibold text-text">Eingetragen — das Konto gibt es schon</h1>
        <p className="text-sm text-text-dim leading-relaxed">
          Deine Eintragung bei {hostName} hat geklappt. Ein Konto mit dieser E-Mail-Adresse
          existiert allerdings bereits — melde dich einfach damit an, ein neues brauchst du nicht.
        </p>
        <Link to="/login" className="block">
          <Button size="lg" fullWidth>
            Zur Anmeldung
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-line bg-surface-1 p-6 text-center space-y-4">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success/15">
        <Check className="h-7 w-7 text-success" />
      </div>
      <h1 className="text-xl font-semibold text-text">Du bist eingetragen</h1>
      <p className="text-sm text-text-dim leading-relaxed">
        {hostName} hat deine Daten jetzt im Netzwerk. Danke dir!
      </p>
      <div className="rounded-xl border border-gold/30 bg-gold/[0.06] p-4 text-left space-y-3">
        <div className="text-sm font-medium text-text">Du kannst dein Profil auch selbst nutzen</div>
        <p className="text-sm text-text-dim leading-relaxed">
          Mit einem eigenen Konto bekommst du dein Netzwerk, KI-Matching auf Basis von „biete“ und
          „suche“ und einen eigenen Einladungslink samt QR-Code.
        </p>
        <Link to="/register" className="block">
          <Button fullWidth>Konto anlegen</Button>
        </Link>
      </div>
    </div>
  );
}
